import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ChartCard, DonutChart } from "@/components/Elements/Charts";
import ListStatisticsCardExtra from "@/components/ListStatistics/ListStatisticsCardExtra";
import {
  useListStatistics,
  useListDonutChartSelected,
} from "@/components/ListStatistics/ListStatisticsHooks";
import { useGetDefaultRiskLevelsWithNoRisk } from "@/entities/risk-matrix";
import { useTranslateNumber } from "@/modules/translate";
import { capitalize } from "@/utilities/string";

const STATS_DATA_KEY = "risk_levels";
const FILTER_KEY = "risk_level";

const ObservationsRiskLevelDonutChart = () => {
  const { t } = useTranslation();
  const { translateNumber } = useTranslateNumber();

  const { riskLevels } = useGetDefaultRiskLevelsWithNoRisk();

  const { loading, fetching, statsData } = useListStatistics({
    statsDataKey: STATS_DATA_KEY,
    filterKey: FILTER_KEY,
  });

  const onFormatName = (keyValue) => {
    const risk = riskLevels.find(({ value }) => `${value}` === keyValue);
    if (!risk) return keyValue;
    return capitalize(risk.name);
  };

  const getRiskLevelCount = useCallback(
    (value) => {
      const riskStats = statsData.find(({ risk_level }) =>
        value === "null" ? !risk_level : risk_level === value
      );
      return riskStats?.count ?? 0;
    },
    [statsData]
  );

  const chartData = useMemo(() => {
    return riskLevels.map(({ value, color }) => ({
      name: `${value}`,
      value: getRiskLevelCount(`${value}`),
      color,
    }));
  }, [riskLevels, getRiskLevelCount]);

  const { selectedLegend, onLegendSelectChange } = useListDonutChartSelected({
    filterKey: FILTER_KEY,
    chartData,
  });

  return (
    <ChartCard
      title={t("observation:statistics.riskLevel.title")}
      extra={<ListStatisticsCardExtra filterKey={FILTER_KEY} />}
    >
      <DonutChart
        id="count-per-risk-level"
        data={chartData}
        loading={loading}
        fetching={fetching}
        controlled={{ selectedLegend }}
        onLegendSelectChange={onLegendSelectChange}
        formatName={onFormatName}
        formatTooltipValue={translateNumber}
      />
    </ChartCard>
  );
};

export default ObservationsRiskLevelDonutChart;
